import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CameraOff, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { decoderStarten, type DecoderStopp } from '@/lib/scanDecoder';
import { EBENE_SCAN } from '@/lib/ebenen';

// Das Scan-Fenster.
//
// Warum überhaupt in der Anwendung und nicht über die Kamera-App: iOS
// öffnet einen Link aus der Kamera-App grundsätzlich in Safari, nie in der
// installierten PWA. Wer am Tablet den Zettel-QR abfotografiert, landet
// also in einem zweiten, nicht angemeldeten Browserfenster. Der Scanner
// hier ist deshalb die Lösung und kein Behelf — er bleibt in der App, in
// der angemeldeten Sitzung.
//
// Die Kamera läuft ausschließlich, solange dieses Fenster offen ist. Beim
// Schließen werden erst der Decoder und dann alle Spuren gestoppt; die
// Kameraleuchte geht aus. Das ist keine Feinheit: eine Kamera, die im
// Hintergrund weiterläuft, ist im Laden ein Vertrauensproblem.
//
// Diese Datei weiß NICHT, was ein Code bedeutet. Sie liefert die rohe
// Zeichenkette und sonst nichts; gedeutet wird beim Aufrufer. Drei gibt
// es inzwischen — Suche, Erfassungsformular, Wareneingang —, und keiner
// davon steht hier.
//
// ZWEI GETRENNTE LEBENSZYKLEN (E4)
//
// Kamera und Decoder hängen bewusst an verschiedenen Effekten. Der Grund
// ist der Auspacktisch: dort wird Buch für Buch gescannt und gebucht,
// und zwischen zwei Büchern darf die Kamera nicht neu anlaufen. Ein
// Neustart kostet Berechtigungsprüfung, getUserMedia, play und beim
// zxing-Weg den Import — pro Buch. Der Decoder dagegen ist billig und
// wird nach jedem Treffer frisch scharf gemacht.

type Zustand = 'startet' | 'laeuft' | 'verweigert' | 'kein-geraet' | 'fehler';

type Props = {
  onSchliessen: () => void;
  /** Der rohe Inhalt des Codes — was er bedeutet, entscheidet der Aufrufer. */
  onCode: (roh: string) => void;
  /**
   * Solange true, liest der Decoder nicht.
   *
   * Der Aufrufer hebt das an, während er einen Treffer zeigt, und senkt
   * es wieder, wenn der Mitarbeiter fertig ist. Die Kamera bleibt dabei
   * an. Wer das Feld gar nicht setzt, bekommt das alte Verhalten: nach
   * jedem Treffer macht sich der Decoder von selbst wieder scharf.
   */
  angehalten?: boolean;
  /** Was über dem Bild steht — Trefferliste, Meldung, Zähler. */
  einblendung?: ReactNode;
  /**
   * Was am OBEREN Rand über dem Bild steht (A22).
   *
   * Getrennt von `einblendung`, weil die beiden verschiedene Fragen
   * beantworten: `einblendung` zeigt, was der Scanner GERADE gefunden
   * hat, und kommt deshalb von unten, wo die Hand liegt. `oben` zeigt,
   * was schon GESCHEHEN ist — es darf den Blick nicht auf sich ziehen
   * und gehört aus dem Weg.
   *
   * Beides gleichzeitig ist der Regelfall am Auspacktisch und stösst
   * nicht aneinander: der Zielrahmen liegt in der Mitte, diese Leiste
   * darüber, die Trefferliste darunter.
   */
  oben?: ReactNode;
  /** Der Text in der Fußleiste. Ohne Angabe der Hinweis für die Suche. */
  hinweis?: ReactNode;
  /**
   * Der Aufrufer hat einen zweiten Eingang neben der Kamera — das
   * Tastaturfeld für den Handscanner (ScanFenster).
   *
   * Dann ist eine fehlende Kamera KEIN Ende, sondern der Normalfall am
   * Büro-PC. Der Kamera-Hinweis wird zur schmalen Zeile am oberen Rand,
   * statt als Kasten mitten über Trefferliste und Knöpfen zu liegen, und
   * er trägt KEIN „Abbrechen": Dieser Knopf schloss das ganze Fenster —
   * samt offener Trefferliste und Kurzjournal — obwohl er nach seinem
   * Platz im Kasten nur den Hinweis wegzuklicken schien (Befund
   * 27.10.2026, Wareneingang am PC).
   */
  tastaturWeg?: boolean;
  /** Beschriftung des Knopfs in der Fußleiste. */
  schliessenText?: string;
  /**
   * Was Escape tut. Ohne Angabe: schließen. Der Aufrufer kann es enger
   * fassen — eine offene Trefferliste zuerst zuklappen statt sofort das
   * ganze Fenster.
   */
  onEscape?: () => void;
};

/**
 * Wurde die Kamera zuletzt abgelehnt?
 *
 * Bewusst ein Modul-Merker und kein Speicher im Browser: er gilt für diese
 * Sitzung der Oberfläche und verhindert genau das, was der Auftrag
 * ausschließt — bei jedem Öffnen erneut zu fragen. Wer die Freigabe in den
 * Systemeinstellungen nachträgt, kommt über "Erneut versuchen" sofort
 * wieder hinein; ein Wert im localStorage würde ihn dagegen über Tage
 * hinweg aussperren.
 */
let letzteAbfuhr = false;

/**
 * Sperrfrist für denselben Code in Millisekunden.
 *
 * Im Dauerbetrieb liegt das Buch nach dem Buchen oft noch eine Sekunde
 * vor der Linse. Ohne diese Frist läse der frisch scharfe Decoder
 * denselben Strichcode sofort wieder und der Auspacktisch bekäme
 * dasselbe Buch ein zweites Mal angeboten. Zwei ECHTE Exemplare
 * desselben Titels liegen weiter auseinander — dazwischen liegt ein
 * Griff in die Kiste und ein Tipp auf "Eingetroffen".
 */
const SPERRE_MS = 2500;

function spurenStoppen(strom: MediaStream): void {
  for (const spur of strom.getTracks()) spur.stop();
}

export function ScanOverlay({
  onSchliessen,
  onCode,
  angehalten = false,
  einblendung,
  oben,
  hinweis,
  tastaturWeg = false,
  schliessenText = 'Abbrechen',
  onEscape
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [zustand, setZustand] = useState<Zustand>(letzteAbfuhr ? 'verweigert' : 'startet');
  // Zählt die Startversuche der KAMERA. 0 heißt: gar nicht erst fragen.
  const [lauf, setLauf] = useState(letzteAbfuhr ? 0 : 1);
  // Zählt die Runden des DECODERS. Jeder Treffer beendet eine Runde.
  const [runde, setRunde] = useState(0);
  // Den Hinweis auf die gesperrte Kamera weggeklickt (nur mit tastaturWeg).
  const [kastenWeg, setKastenWeg] = useState(false);

  // Der Treffer-Rückruf steckt in einer Referenz, damit ein neuer
  // Aufrufer-Rückruf nicht die Kamera neu startet.
  const codeRef = useRef(onCode);
  useEffect(() => {
    codeRef.current = onCode;
  }, [onCode]);

  // Was zuletzt gelesen wurde und wann — für die Sperrfrist oben.
  const zuletzt = useRef<{ roh: string; zeit: number }>({ roh: '', zeit: 0 });

  // --- Kamera -------------------------------------------------------
  useEffect(() => {
    if (lauf === 0) return;

    let abgebrochen = false;
    let strom: MediaStream | null = null;
    // Das Video-Element EINMAL festhalten. Beim Aufräumen darf nicht mehr
    // in die Referenz geschaut werden — dort steht dann womöglich schon
    // ein anderes (oder gar keins), und die Spur bliebe an.
    const el = videoRef.current;

    async function starten(): Promise<void> {
      const geraete = navigator.mediaDevices;
      if (geraete === undefined || typeof geraete.getUserMedia !== 'function') {
        setZustand('kein-geraet');
        return;
      }

      // Vorab fragen, wo der Browser die Frage beantwortet: eine schon
      // erteilte Absage soll keinen neuen Systemdialog auslösen. Safari
      // kennt die Abfrage für die Kamera nicht — dort greift der Merker
      // oben zusammen mit dem Fehler aus getUserMedia.
      try {
        const stand = await navigator.permissions?.query({
          name: 'camera' as PermissionName
        });
        if (stand?.state === 'denied') {
          letzteAbfuhr = true;
          setZustand('verweigert');
          return;
        }
      } catch {
        // Kennt der Browser nicht — dann eben direkt fragen.
      }

      try {
        // "environment" ist die Rückkamera. Als Wunsch, nicht als
        // Bedingung (kein `exact`): ein Bürorechner hat nur eine Kamera,
        // und mit `exact` bekäme er gar keine.
        // Auflösung ausdrücklich erbitten (Befund 29.10.2026). Ohne
        // Angabe wählt der Browser selbst, häufig 640×480 — im Hochformat
        // dann 480 Pixel Breite (am iPhone nicht nachgemessen).
        // Die schmalen Code-128-Streifen auf den Barsortiments-Etiketten
        // brauchen rund 2 Pixel je Modul: am Foto des Betreibers las
        // zxing einen Strichcode ab 230 Pixel Breite, darunter nicht mehr.
        // Bei 480 Pixeln müsste er das halbe Bild füllen. `ideal` ist
        // eine Bitte, keine Bedingung — kann die Kamera es nicht, nimmt
        // der Browser die nächstbeste.
        strom = await geraete.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
        });
      } catch (e) {
        const name = e instanceof DOMException ? e.name : '';
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          letzteAbfuhr = true;
          setZustand('verweigert');
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          setZustand('kein-geraet');
        } else {
          setZustand('fehler');
        }
        return;
      }

      if (abgebrochen || el === null) {
        spurenStoppen(strom);
        strom = null;
        return;
      }

      letzteAbfuhr = false;
      el.srcObject = strom;
      try {
        await el.play();
      } catch {
        // Manche Browser spielen erst nach einer Benutzergeste. Der Klick
        // auf den Scan-Knopf war eine — und wenn nicht, zeigt das Bild
        // trotzdem, sobald Daten kommen.
      }
      if (abgebrochen) return;

      setZustand('laeuft');
    }

    void starten();

    return () => {
      abgebrochen = true;
      if (strom !== null) spurenStoppen(strom);
      if (el !== null) el.srcObject = null;
    };
  }, [lauf]);

  // --- Decoder ------------------------------------------------------
  //
  // Läuft nur, wenn die Kamera läuft und niemand angehalten hat. Nach
  // einem Treffer erhöht sich `runde` und dieser Effekt läuft neu — mit
  // einem frischen Decoder, aber demselben Kamerastrom.
  useEffect(() => {
    if (zustand !== 'laeuft' || angehalten) return;
    const el = videoRef.current;
    if (el === null) return;

    let abgebrochen = false;
    let stopp: DecoderStopp | null = null;

    async function scharfMachen(): Promise<void> {
      try {
        stopp = await decoderStarten(el as HTMLVideoElement, (roh) => {
          if (abgebrochen) return;
          const jetzt = Date.now();
          if (roh === zuletzt.current.roh && jetzt - zuletzt.current.zeit < SPERRE_MS) {
            // Derselbe Code, zu früh. Nicht melden, aber eine neue Runde
            // beginnen — sonst bliebe der Decoder stumm stehen.
            setRunde((r) => r + 1);
            return;
          }
          zuletzt.current = { roh, zeit: jetzt };
          setRunde((r) => r + 1);
          codeRef.current(roh);
        });
      } catch {
        setZustand('fehler');
      }
      if (abgebrochen) stopp?.();
    }

    void scharfMachen();

    return () => {
      abgebrochen = true;
      stopp?.();
    };
  }, [zustand, runde, angehalten]);

  // Escape schließt — am Bürorechner der schnellste Weg heraus. Hat der
  // Aufrufer eine engere Bedeutung (Trefferliste zuklappen), gilt die.
  useEffect(() => {
    function taste(e: KeyboardEvent): void {
      if (e.key === 'Escape') (onEscape ?? onSchliessen)();
    }
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [onSchliessen, onEscape]);

  const kameraFehlt =
    zustand === 'verweigert' || zustand === 'kein-geraet' || zustand === 'fehler';
  // Mit Handscanner-Weg: „keine Kamera" und „startet nicht" sind nur
  // eine Zeile. Eine GESPERRTE Kamera behält den Kasten — dort steht,
  // wie man sie freigibt, und das passt in keine Zeile. Der Kasten
  // weicht aber jeder Trefferliste und lässt sich wegklicken.
  const schmaleKameraZeile = tastaturWeg && (zustand === 'kein-geraet' || zustand === 'fehler');
  const kastenSichtbar =
    kameraFehlt &&
    !schmaleKameraZeile &&
    !(tastaturWeg && (kastenWeg || einblendung !== undefined));

  function nochmal(): void {
    setKastenWeg(false);
    setZustand('startet');
    setLauf((l) => l + 1);
  }

  // An den <body> gehaengt, nicht an die Stelle im Baum.
  //
  // Der z-index allein haette den Befund nicht behoben: Ein Element kann
  // nur innerhalb SEINES Stapelkontexts steigen. Wird das Scan-Fenster aus
  // dem Erfassungs-Sheet heraus geoeffnet, sitzt es im Baum unter dessen
  // Aufrufer — und Radix haengt den Sheet-Inhalt zugleich in ein eigenes
  // Portal an den <body>. Beide auf z-50, und wer spaeter im Dokument
  // steht, gewinnt: das Sheet. Gemessen im Pruefstand mit
  // document.elementFromPoint(): Das Scan-Fenster war 820x1180 gross,
  // sichtbar und display:flex — und trotzdem lag an jedem Punkt das Sheet
  // obenauf.
  //
  // Mit eigenem Portal haengen beide am selben Ort, und dann entscheidet
  // die Ebene (lib/ebenen.ts) statt der Zufall der Baumstellung.
  const fenster = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Code scannen"
      data-tour="scan-fenster"
      // `pointer-events-auto` ist hier kein Zierrat, sondern die zweite
      // Haelfte des Fixes. Radix setzt, solange sein Dialog offen ist,
      // `pointer-events: none` auf den <body> und hebt es nur fuer den
      // eigenen Inhalt wieder auf. Ohne diese Zeile laege das Scan-Fenster
      // zwar sichtbar oben, waere aber fuer den Finger durchsichtig: Der
      // Tipp ginge durch die Kamera hindurch ins Formular dahinter.
      // Gemessen im Pruefstand — body pointer-events: none, Sheet auto,
      // Scan-Fenster geerbt none.
      className={`fixed inset-0 ${EBENE_SCAN} flex flex-col bg-ink/95 pointer-events-auto`}
    >
      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          // Ohne feste Höhe zieht das Video die Seite auf; object-cover
          // füllt den Rahmen, ohne das Bild zu verzerren.
          className="h-full w-full object-cover"
        />

        {zustand === 'laeuft' && einblendung === undefined && (
          // Ein Rahmen, der zeigt, wohin gehalten werden soll. Rein
          // dekorativ — gelesen wird das ganze Bild, nicht nur dieser
          // Ausschnitt. Deshalb aria-hidden.
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <div className="h-56 w-72 rounded-lg border-2 border-cream/70" />
          </div>
        )}

        {zustand === 'startet' && (
          <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-cream/80">
            Kamera wird geöffnet …
          </p>
        )}

        {(oben !== undefined || schmaleKameraZeile) && (
          // Am oberen Rand, mit eigenem Rollbereich und gedeckelter
          // Höhe: der Zielrahmen in der Mitte darf nicht zugedeckt
          // werden. Die Kamera selbst wird dabei NICHT kleiner — die
          // Leiste liegt über dem Bild, sie nimmt ihm keinen Platz.
          //
          // 30 %, und die Zahl ist gemessen: Bei 34 % stiess die volle
          // Liste auf 1024 × 768 genau an die Oberkante des Zielrahmens
          // (Unterkante 238 px, Rahmen ab 238 px). Bei 30 % bleiben dort
          // 28 und auf 390 × 844 44 Pixel Luft.
          <div className="absolute inset-x-0 top-0 max-h-[30%] overflow-y-auto px-2 pt-2 sm:px-4">
            <div className="mx-auto w-full max-w-lg space-y-2">
              {schmaleKameraZeile && (
                // Die Kamera fehlt, der Handscanner ist da: eine Zeile,
                // kein Kasten. Sie liegt am oberen Rand und deckt weder
                // die Trefferliste noch das Tastaturfeld zu.
                <div
                  role="status"
                  className="flex items-center gap-2 rounded-md border border-cream/25 bg-ink/80 px-3 py-1.5 text-xs text-cream/80"
                >
                  <CameraOff aria-hidden className="h-4 w-4 shrink-0 text-cream/60" />
                  <span className="min-w-0 flex-1">
                    {zustand === 'kein-geraet'
                      ? 'Keine Kamera — Handscanner oder Eingabe unten benutzen.'
                      : 'Kamera startet nicht — Handscanner oder Eingabe unten benutzen.'}
                  </span>
                  <button
                    type="button"
                    onClick={nochmal}
                    className="inline-flex min-h-[44px] shrink-0 items-center rounded-md px-2 text-xs text-cream/80 underline-offset-2 hover:text-cream hover:underline"
                  >
                    Kamera erneut suchen
                  </button>
                </div>
              )}
              {oben}
            </div>
          </div>
        )}

        {einblendung !== undefined && (
          // Der Aufrufer blendet ein, was zum Treffer zu sagen ist. Von
          // unten, damit die Hand am Tablet nicht über den Text greifen
          // muss, und mit eigenem Rollbereich: eine lange Trefferliste
          // darf nicht das Fenster aufziehen.
          <div className="absolute inset-x-0 bottom-0 max-h-[70%] overflow-y-auto px-0 sm:px-4">
            <div className="mx-auto w-full max-w-lg">{einblendung}</div>
          </div>
        )}

        {kastenSichtbar && (
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="max-w-sm rounded-md border border-line bg-cream p-4 text-sm text-ink">
              <p className="mb-2 flex items-center gap-2 font-serif text-base">
                <CameraOff className="h-5 w-5 text-coral" />
                {zustand === 'verweigert'
                  ? 'Die Kamera ist gesperrt'
                  : zustand === 'kein-geraet'
                    ? 'Keine Kamera gefunden'
                    : 'Der Scanner ließ sich nicht starten'}
              </p>
              {zustand === 'verweigert' ? (
                <div className="space-y-2 text-ink-soft">
                  <p>Du musst der Anwendung einmal erlauben, die Kamera zu benutzen.</p>
                  <p>
                    <span className="text-ink">Auf iPad und iPhone:</span> Einstellungen →
                    Safari → Kamera auf „Fragen“ oder „Erlauben“ stellen. Im Browser geht es
                    auch über das „aA“-Menü links in der Adresszeile →
                    Website-Einstellungen.
                  </p>
                  <p>
                    <span className="text-ink">Am Rechner:</span> auf das Schloss- oder
                    Kamerasymbol in der Adresszeile klicken und die Kamera freigeben.
                  </p>
                </div>
              ) : zustand === 'kein-geraet' ? (
                <p className="text-ink-soft">
                  Dieses Gerät hat keine nutzbare Kamera. Tipp die Nummer oder die ISBN
                  stattdessen ins Suchfeld.
                </p>
              ) : (
                <p className="text-ink-soft">
                  Versuch es noch einmal. Bleibt es dabei, hilft das Suchfeld weiter.
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <Button variant="primary" onClick={nochmal}>
                  Erneut versuchen
                </Button>
                {tastaturWeg ? (
                  // Schließt NUR den Hinweis. Das Fenster mit Trefferliste,
                  // Kurzjournal und Handscanner-Feld bleibt, wie es ist.
                  <Button variant="ghost" onClick={() => setKastenWeg(true)}>
                    Ohne Kamera weiter
                  </Button>
                ) : (
                  <Button variant="ghost" onClick={onSchliessen}>
                    Abbrechen
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Die Fußleiste bleibt in JEDEM Zustand bedienbar — auch wenn die
          Kamera gesperrt ist. Ein Fenster, aus dem man nicht mehr
          herauskommt, ist schlimmer als kein Scanner. */}
      <div className="flex items-center justify-between gap-3 border-t border-ink-soft/40 bg-ink px-4 py-3">
        {hinweis ?? (
          <p className="flex items-center gap-2 text-sm text-cream/90">
            <ScanLine aria-hidden className="h-5 w-5 shrink-0" />
            QR vom Zettel oder Buch-Barcode scannen
          </p>
        )}
        <button
          type="button"
          onClick={onSchliessen}
          className="inline-flex min-h-[44px] shrink-0 items-center rounded-md border border-cream/40 px-4 text-sm text-cream hover:bg-cream/10"
        >
          {schliessenText}
        </button>
      </div>
    </div>
  );

  // Im Test laeuft jsdom mit einem document; auf dem Server gaebe es
  // keines. Der Rueckfall haelt die Komponente auch dort gutmuetig.
  return typeof document === 'undefined' ? fenster : createPortal(fenster, document.body);
}
